import { isString } from 'lodash-es';

import {
  automaticColoringOptions,
  divergingColoringOptions,
  isCustomColorPalette,
  sequentialColoringOptions,
  themedColoringOptions,
  type ColorPalette,
  type CustomColorPalette,
} from '../../core/types/color-palette.js';
import type { ColoredRange } from '../../core/types/colored-range.js';
import {
  DEFAULT_CATEGORICAL_COLOR_PALETTE,
  DEFAULT_RANGE_COLOR_PALETTE,
} from '../constants.js';

const categoricalPalettes = new Set<string>([
  ...themedColoringOptions,
  ...automaticColoringOptions,
]);

const rangePalettes = new Set<string>([
  ...sequentialColoringOptions,
  ...divergingColoringOptions,
]);

const isCategoricalColorPalette = (
  colorPalette: unknown,
): colorPalette is ColorPalette => {
  return isString(colorPalette) && categoricalPalettes.has(colorPalette);
};

const isRangeColorPalette = (
  colorPalette: unknown,
): colorPalette is ColorPalette => {
  return isString(colorPalette) && rangePalettes.has(colorPalette);
};

const isCustomRanges = (
  colorPalette: ColorPalette | CustomColorPalette | ColoredRange[],
) => {
  return (
    Array.isArray(colorPalette) &&
    !!colorPalette.length &&
    colorPalette.every(
      (color) => typeof color === 'object' && 'from' in color && 'to' in color,
    )
  );
};

const isCustomColoredArray = (
  colorPalette: ColorPalette | CustomColorPalette | ColoredRange[],
) =>
  Array.isArray(colorPalette) &&
  colorPalette.length &&
  colorPalette.every((color) => typeof color === 'string');

export const getTreeMapColorPalette = (
  colorPalette: ColorPalette | CustomColorPalette | ColoredRange[],
) => {
  const isCategoricalPalette = isCategoricalColorPalette(colorPalette);
  const isRangePalette = isRangeColorPalette(colorPalette);
  const isValidPalette = isCategoricalPalette || isRangePalette;
  const isCustomRangePalette = isCustomRanges(colorPalette);
  const isCustomCategoricalPalette =
    !isCustomRangePalette &&
    !Array.isArray(colorPalette) &&
    isCustomColorPalette(colorPalette);

  const isValidRangePalette = isValidPalette || isCustomRangePalette;

  const isValidCategoricalPalette =
    isValidPalette ||
    isCustomCategoricalPalette ||
    isCustomColoredArray(colorPalette);

  const categoricalColorPalette = isValidCategoricalPalette
    ? (colorPalette as ColorPalette | CustomColorPalette)
    : DEFAULT_CATEGORICAL_COLOR_PALETTE;

  const rangeColorPalette = isValidRangePalette
    ? (colorPalette as ColorPalette | ColoredRange[])
    : DEFAULT_RANGE_COLOR_PALETTE;

  return { categoricalColorPalette, rangeColorPalette };
};
